import { Add, Close, DeleteOutline, Download, FilterAltOutlined, LinkOff } from "@mui/icons-material";
import { Alert, AlertTitle, Badge, Box, Button, Chip, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Grid2 as Grid, IconButton, InputLabel, Menu, MenuItem, Select, Stack, Tooltip, Typography } from "@mui/material";
import { GridRowSelectionModel, GridSortDirection, GridSortModel } from "@mui/x-data-grid-premium";
import { useSnackbar } from "notistack";
import React from "react";
import { useNavigate } from "react-router-dom";
import { CustomDataGrid, DeletePanel, DocumentActions, DocumentShare, EntityPublish, Folders, folderState, ModuleMapper, MyPaper, ObjectType, SearchFilter } from "..";
import { useToggleView } from "../../hooks";
import { Association, ILinkOrUnlink, LinkFilter, SelectBoxOption, TagInfo } from "../../interfaces";
import { useAppDispatch, useAppSelector } from "../../redux";
import { closeBackdrop, openBackdrop } from "../../redux/slices/backdrop";
import { closeConfirmationDialog, openConfirmationDialog, updateConfirmationDialogPositiveBtn, updateCongirmationDialogBody } from "../../redux/slices/confirmationDialog";
import { closeDialog, openDialog } from "../../redux/slices/dialog";
import { closeDrawer, openDrawer } from "../../redux/slices/drawer";
import { linksData } from "../../redux/slices/material";
import { clearSelectedChip, DepKeys, selectAllChip, SelectedDependency } from "../../redux/slices/utils";
import { HasAccess } from "../../router/authorization";
import { PERMISSIONS } from "../../router/permission";
import { AppRoutes } from "../../router/routes";
import { ViewLinkDocument } from "../../screens";
import { versionDropdownOption } from "../../screens/documents/documents";
import { getEmptyDrawioDiagram } from "../../screens/drawioDiagrams/utils";
import { Transition } from "../../screens/viewBOM/basicDetails";
import { DataTableForm } from "../../screens/viewMaterial/dataSheet";
import { CapitalizeString, DEFAULT_BOARD_VIEW_PAGINATION, formatBytes, TAG_CANONICAL_NAMES, UUIDv4 } from "../../utils";
import { BoardLayout } from "../boardLayout";
import { IBoard, IItem } from "../boardLayout/types";
import { Filters } from "../filter";
import { AddEditDocument, FileSelectionOptionType, MultipleDoc } from "./addEditDocument";
import { DocumentTableColumn, fileSelectOptions, giveMeLinksAndUnlinksForDocuments, giveMeLocationAndDocumentTags } from "./utils";
import { CurrentView } from "../boardLayout/components/currentView";
import { PublishedIcon } from "../../images/svg/publishedIcon";
import { WIPIcon } from "../../images/svg/WIPIcon";
import GreyAccordian from "../greyAccordian";
import { useBulkDownloadDocumentsMutation, useDeleteMaterialDocumentMutation, useDuplicateDocumentMutation, useLazyGetDependenciesQuery, useLazyGetDocumentByIdQuery, useLazyGetDocumentsQuery, useLazyGetTagGroupsQuery, useLazyGetTagsBasedOnParameterQuery, useLinkModuleMutation, useLinkOrUnlinkEntityMutation, useUnlinkModuleMutation, useUpdateDocumentEditStatusMutation, useUpdateMaterialDocumentMutation } from "../../redux/services";

interface State { objectType: string, objectId: string };

export interface UploadConfig { accept: string, fileTypes: string, placeholderText: string };

export interface IDoc {
    associations: Association[],
    _id: string;
    sequence_id: string;
}

const uploadContentDetails = {
    "pdf": {
        accept: "application/pdf",
        fileTypes: "PDF",
        placeholderText: "pdf"
    },
    "image": {
        accept: "image/jpeg, image/png, image/jpg",
        fileTypes: "JPEG, JPG, PNG",
        placeholderText: "image"
    },
};

const LinkTo = ({ refetch, links, clearSelectionHandler, documents = [], shouldMove = false }: { links: GridRowSelectionModel, clearSelectionHandler: () => void, shouldMove?: boolean, documents: IDoc[], refetch: () => void }) => {
    const dispatch = useAppDispatch();

    const [linkOrUnlinkDocuments] = useLinkOrUnlinkEntityMutation();

    const { enqueueSnackbar } = useSnackbar();

    const [state, setState] = React.useState<State>({ objectType: '', objectId: '', });
    const [selectedFolder, setSelectedFolder] = React.useState<folderState | undefined>(undefined);

    const onLinkBtnClicked = async () => {
        const action = shouldMove ? "move" : "link";
        const successMessage = shouldMove ? "Moved" : "Linked";
        dispatch(openBackdrop(`${shouldMove ? "Moving" : "Linking"} Document...`));

        const validFolder = selectedFolder?._id && selectedFolder?._id !== "root";

        const filteredDocs = documents.filter(doc => links.includes(doc?._id));

        let primaryAssociations: ILinkOrUnlink[] = [];
        filteredDocs.forEach(doc => {
            doc.associations.forEach(association => {
                primaryAssociations.push({
                    object_ids: association.object_details.map(d => d?._id),
                    object_type: association.object_type
                })
            })
        })

        let res: any = {};
        if (validFolder) {
            if (shouldMove) {
                res = await linkOrUnlinkDocuments({
                    links: [],
                    primary: [
                        ...primaryAssociations
                    ],
                    unlinks: [
                        { object_ids: links as string[], object_type: "document" },
                    ]
                })

                if (res?.error) {
                    enqueueSnackbar(`Unable to ${action} the document. Info: ${res.error?.data?.title ?? res.error?.data ?? "Unknown"}`, { variant: "error" });
                    dispatch(closeBackdrop());
                    dispatch(closeDialog());
                    return;
                }
            }

            await linkOrUnlinkDocuments({
                links: [
                    { object_ids: links as string[], object_type: "document" },
                ],
                primary: [
                    { object_ids: [selectedFolder?._id], object_type: "folder" },
                    { object_ids: [state.objectId], object_type: state.objectType }
                ],
                unlinks: []
            }).then((res: any) => {
                if (res.hasOwnProperty('error')) {
                    enqueueSnackbar(`Unable to ${action} the document. Info: ${res.error?.data?.title ?? res.error?.data ?? "Unknown"}`, { variant: "error" })
                } else {
                    enqueueSnackbar(`Documents ${successMessage} Successfully!!!`, { variant: "success" });

                }
            }).catch((err: any) => {
                enqueueSnackbar(`Unable to ${action} the document. Info: ${err?.data?.title ?? "Unknown"}`, { variant: "warning" })
            });
        } else {
            if (shouldMove) {
                res = await linkOrUnlinkDocuments({
                    links: [],
                    primary: [
                        ...primaryAssociations
                    ],
                    unlinks: [
                        { object_ids: links as string[], object_type: "document" },
                    ]
                })

                if (res?.error) {
                    enqueueSnackbar(`Unable to ${action} the document. Info: ${res.error?.data?.title ?? res.error?.data ?? "Unknown"}`, { variant: "error" });
                    dispatch(closeBackdrop());
                    dispatch(closeDialog());
                    return;
                }
            }

            res = await linkOrUnlinkDocuments({
                links: [
                    { object_ids: links as string[], object_type: "document" },
                ],
                primary: [
                    { object_ids: [state.objectId], object_type: state.objectType }
                ],
                unlinks: []
            })

            if (res?.error) {
                enqueueSnackbar(`Unable to ${action} the document. Info: ${res.error?.data?.title ?? res.error?.data ?? "Unknown"}`, { variant: "error" })
            } else {
                enqueueSnackbar(`Documents ${successMessage} Successfully!!!`, { variant: "success" });
                clearSelectionHandler();
            }
        }
        dispatch(closeBackdrop());
        dispatch(closeDialog());
        refetch();
    };

    const onFolderSelected = async (...args: any) => {
        setSelectedFolder(args?.[0]);
    };

    return <>
        {/* Heading */}
        <DialogTitle>
            <Typography
                variant="body1"
                sx={{
                    fontFamily: "htrts_medium",
                    borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                    pb: 1
                }}>
                {shouldMove ? "Move" : "Link"} Documents
            </Typography>
        </DialogTitle>

        <DialogContent>
            <>
                <ModuleMapper
                    associations={[]}
                    onOptionChange={(object_type, object_id) => {
                        setState((prevState) => ({ ...prevState, objectType: object_type as string, objectId: (object_id ?? '') as string }))
                    }}
                    shouldSkip
                />

                {state.objectId && state.objectType && (
                    <Stack mt={2} spacing={0.5}>
                        <Typography
                            variant="body2"
                            color="primary"
                        >
                            Folder
                            <Typography color="error" variant="caption">
                                *
                            </Typography>
                        </Typography>
                        <Folders
                            object_id={state.objectId}
                            object_type={state.objectType as ObjectType}
                            showCreateBtn={false}
                            creatable={false}
                            updatable={false}
                            deletable={false}
                            readable={true}
                            publishable={false}
                            hideDocuments={true}
                            hideShadow={true}
                            onFolderSelected={onFolderSelected}
                        />
                    </Stack>
                )}
            </>
        </DialogContent>

        <DialogActions>
            <Grid
                container
                spacing={2}
                width={"100%"}
                sx={{
                    justifyContent: "center",
                    alignItems: "center",
                    px: 2,
                    pb: 1,
                    pt: 4
                }}>
                {/* Cancel Btn */}
                <Grid
                    size={{
                        xs: 12,
                        sm: 6,
                        md: 6,
                        lg: 4
                    }}>
                    <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => dispatch(closeDialog())}
                    >
                        Cancel
                    </Button>
                </Grid>

                {/* Next Btn */}
                <Grid
                    id="link-entity"
                    size={{
                        xs: 12,
                        sm: 6,
                        md: 6,
                        lg: 4
                    }}>
                    <Button
                        fullWidth
                        variant="contained"
                        onClick={onLinkBtnClicked}
                        disabled={!state.objectId}
                    >
                        {shouldMove ? "Move" : "Link"}
                    </Button>
                </Grid>
            </Grid>
        </DialogActions>
    </>;
};

export const Documents: React.FC<{
    children?: JSX.Element, refetch: any, Id: string, _id: string, objectType: string, onAnnotate?: (id: string) => void,
    documentData?: any, linkDocument?: any, associations?: any, tag_id?: string | null, tag_group_id?: string | null,
    showCreateBtn?: boolean, showHelperBtn?: boolean, hideDeleteBtn?: boolean, isPdfOnly?: boolean, unlinkDoc?: (id: string, refetch: any, row: any) => void,
    parentData?: LinkFilter, creatable?: boolean, updatable?: boolean, deletetable?: boolean, publishable?: boolean, readable?: boolean, onRegenerate?: any, onDocumentUploadComplete?: any,
    showSearch?: boolean, showFilter?: boolean, showRevision?: boolean, hasCheckbox?: boolean, folderId?: string, includeAssoc?: string[], hasBoardView?: boolean
}> = ({
    showCreateBtn = true, showHelperBtn = true, hideDeleteBtn = false, isPdfOnly = false, showSearch = true,
    showFilter = true, showRevision = true, hasCheckbox = false, includeAssoc = [], hasBoardView = true, ...props
}) => {

        const dispatch = useAppDispatch();
        const { enqueueSnackbar } = useSnackbar();
        const navigate = useNavigate();
        const uploads = useAppSelector(store => store.utils.uploads);
        const { perm } = useAppSelector(store => store.auth.userDetails);
        const { selectedChip } = useAppSelector(store => store.utils);

        const [getDocuments, { data: allData = { data: [], totalCount: 0 }, isLoading, isError, isFetching }] = useLazyGetDocumentsQuery({});
        const [deleteDocumentMutation] = useDeleteMaterialDocumentMutation();
        const [duplicateDocument] = useDuplicateDocumentMutation();
        const [updateDocumentEditStatus] = useUpdateDocumentEditStatusMutation();
        const [getDependencies] = useLazyGetDependenciesQuery();
        const [downloadDocuments, docBulkDownloadRes] = useBulkDownloadDocumentsMutation();
        const [getDocumentAPI] = useLazyGetDocumentByIdQuery();
        const [linkDocument] = useLinkModuleMutation();
        const [unlinkDoc] = useUnlinkModuleMutation();
        const [updateDocument] = useUpdateMaterialDocumentMutation();
        const [getTagGroups, tagGroupResponse] = useLazyGetTagGroupsQuery();
        const [getTags, tagsResponse] = useLazyGetTagsBasedOnParameterQuery();

        const loading = isLoading || isFetching;

        const tagsLoading = tagGroupResponse.isFetching || tagGroupResponse.isLoading;

        const [paginationModel, setPaginationModel] = React.useState({ page: 0, pageSize: 100 });
        const [search, setSearch] = React.useState("");
        const [dropDown, updateDropDown] = React.useState<SelectBoxOption>(versionDropdownOption[0]);
        const [searchByKeyword, setSearchByKeyword] = React.useState("");
        const [rowSelectionModel, setRowSelectionModel] = React.useState<GridRowSelectionModel>([]);
        const [queryOptions, setQueryOptions] = React.useState({ sortModel: [{ field: 'sequence_id', sort: 'desc' as GridSortDirection }] });
        const [menuEl, setMenuEl] = React.useState<null | HTMLElement>(null);
        const [isPublished, setIsPublished] = React.useState(false);
        const [openDs, setOpenDs] = React.useState<{ row: any, board?: IBoard } | null>(null);
        const [openErrorAlert, setOpenErrorAlert] = React.useState(true);


        const [key, setKey] = React.useState<any>('');
        const { isBoardView, selectedBoard } = useToggleView("document");
        const boardDetails = useAppSelector(store => store.board);
        const items: IItem[] = openDs && isBoardView ? boardDetails?.itemsByBoard?.[(openDs as any)?.board?.id] ?? [] : [];

        const { data, totalCount } = allData;

        const sheet_id = (isBoardView ? items : data)?.find(d => d?._id === (openDs as any)?.row?._id)?.associations?.find((_: any) => _.object_type === "dataTableForm")?.object_details?.at(-1)?._id || "New";

        const creatable = React.useMemo(() => props.updatable && HasAccess(perm, PERMISSIONS.DOCUMENT_CREATE), [perm, props.updatable]);
        const readable = React.useMemo(() => props?.readable && HasAccess(perm, PERMISSIONS.DOCUMENT_READ), [perm, props?.readable]);
        const updatable = React.useMemo(() => props.updatable && HasAccess(perm, PERMISSIONS.DOCUMENT_UPDATE), [perm, props.updatable]);
        const deletable = React.useMemo(() => props.deletetable && HasAccess(perm, PERMISSIONS.DOCUMENT_DELETE), [perm, props.deletetable]);
        const publishable = React.useMemo(() => props.publishable && HasAccess(perm, PERMISSIONS.DOCUMENT_PUBLISH), [perm, props.publishable]);

        const open = Boolean(menuEl);

        const boardTypes = (tagsResponse?.data?.tags ?? []).map((t: any) => ({
            ...t, id: t._id, title: t.tag_name, description: t.tag_desc
        }));

        const refetchTagGroups = () => {
            getTagGroups({ ...DEFAULT_BOARD_VIEW_PAGINATION, scope: "document" });
        };

        const refetchAfterUpdate = (isBoardView: boolean) => {
            if (isBoardView) {
                refetchTagGroups();
                setKey(UUIDv4());
            }
        }

        const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => setMenuEl(event.currentTarget);

        const handleClose = () => setMenuEl(null);

        const handleDsClose = () => {
            setOpenDs(null);
        };

        const handleSortModelChange = React.useCallback((sortModel: GridSortModel) => {
            // Here you save the data you need from the sort model
            setQueryOptions({ sortModel: [...sortModel] });
        }, []);

        const object_id = props.folderId ? props.folderId : props._id;
        const object_type = props.folderId ? 'folder' : props.objectType;
        const isCommonModule = props.objectType === "";

        const refetch = (o_id?: string, o_type?: string) => {
            getDocuments({
                include_all: true,
                include_history: dropDown?.value ?? false,
                include_assoc: includeAssoc,
                object_id: o_id || object_id,
                object_type: o_type || object_type,
                tag_id: props.tag_id,
                tag_group_id: props.tag_group_id,
                sort_by: queryOptions?.sortModel?.[0]?.field,
                sort_order: queryOptions?.sortModel?.[0]?.sort,
                search,
                // exclude_assoc: props.folderId ? [] : ['folder'],
                ...paginationModel
            });
        };

        const refetchAll = () => {
            refetch();
            props.refetch();
            if (props?.onRegenerate) {
                props.onRegenerate();
            }
            setKey(UUIDv4());
        };

        const openAddEdit = async (data: any, isEdit: boolean = true, selectedOption?: FileSelectionOptionType) => {
            if (updatable) {
                if (isEdit) {
                    dispatch(openDialog({
                        title: "",
                        hideNegativeBtn: true,
                        hidePositiveBtn: true,
                        enablePadding: false,
                        body: <AddEditDocument
                            uploadContentDetail={isPdfOnly ? uploadContentDetails["pdf"] : undefined}
                            isEdit={isEdit}
                            data={data}
                            objectType={props.objectType}
                            refetch={refetchAll}
                            _id={props._id}

                            hideUpload={data.document_type === "drawing" || data.document_type === "IEOffice"}
                            showFileName={data.document_type === "drawing" || data.document_type === "IEOffice"}
                            showModuleMapper={data.document_type === "drawing" || data.document_type === "IEOffice"}
                            documentMode={CapitalizeString(data.document_type)}
                            onDocumentUploadComplete={props.onDocumentUploadComplete}
                            showBOM={data.document_type === "drawing"}
                        />
                    }));
                } else {
                    if (selectedOption && selectedOption === "single") {
                        dispatch(openDialog({
                            title: "",
                            hideNegativeBtn: true,
                            hidePositiveBtn: true,
                            enablePadding: false,
                            body: <AddEditDocument
                                uploadContentDetail={isPdfOnly ? uploadContentDetails["pdf"] : undefined}
                                isEdit={isEdit}
                                data={data}
                                objectType={props.objectType}
                                refetch={refetchAll}
                                folderId={props.folderId}
                                _id={props._id}
                                onDocumentUploadComplete={props.onDocumentUploadComplete}
                            />
                        }));
                    } else if (selectedOption && selectedOption === "multiple") {
                        dispatch(openDialog({
                            title: "",
                            hideNegativeBtn: true,
                            hidePositiveBtn: true,
                            maxWidth: "lg",
                            enablePadding: false,
                            body: <MultipleDoc
                                onDocumentUploadComplete={props.onDocumentUploadComplete}
                                uploadContentDetail={isPdfOnly ? uploadContentDetails["pdf"] : undefined}
                                isEdit={isEdit}
                                data={data}
                                objectType={props.objectType}
                                refetch={refetchAll}
                                folderId={props.folderId}
                                _id={props._id}
                                files={[]} />,
                        }))
                    } else if (selectedOption && selectedOption === "iedraw") {
                        const defaultFile = await getEmptyDrawioDiagram();
                        dispatch(openDialog({
                            title: "",
                            hideNegativeBtn: true,
                            hidePositiveBtn: true,
                            enablePadding: false,
                            body: <AddEditDocument
                                isEdit={isEdit}
                                data={data}
                                objectType={props.objectType}
                                refetch={refetchAll}
                                folderId={props.folderId}
                                _id={props._id}

                                hideUpload={true}
                                showFileName={true}
                                showModuleMapper={true}
                                documentMode="Drawing"

                                defaultFile={defaultFile}
                                defaultFileType={".xml"}
                                showBOM
                                onDocumentUploadComplete={props.onDocumentUploadComplete}
                            />
                        }));
                    } else if (selectedOption && selectedOption === "iedocs") {
                        dispatch(openDialog({
                            title: "",
                            hideNegativeBtn: true,
                            hidePositiveBtn: true,
                            enablePadding: false,
                            body: <AddEditDocument
                                _id={props._id}
                                isEdit={isEdit}
                                data={data}
                                objectType={props.objectType}
                                refetch={refetchAll}
                                folderId={props.folderId}
                                onDocumentUploadComplete={props.onDocumentUploadComplete}
                                hideUpload={true}
                                showFileName={true}
                                showModuleMapper={true}
                                documentMode="IEOffice"
                            />
                        }));
                    }
                }
            }
        };

        const deleteDocument = (document: any, isBoardView: boolean) => {
            let message = `Check for dependency before deleting the Document ${document.sequence_id}-r${document.version}?`;
            dispatch(openConfirmationDialog({
                title: "Delete Operation",
                body: <DeletePanel
                    message={message}
                    warningMessage={""}
                />,
                positiveBtn: "Check for dependencies",
                negativeBtn: "Cancel",
                onOk: () => performRowDel(document, false, isBoardView),
                onNegativeBtn: () => dispatch(closeConfirmationDialog())
            }))
        };

        const performRowDel = async (document: any, force: boolean = false, isBoardView: boolean) => {
            dispatch(openBackdrop("Checking Dependencies and deleting document..."));
            let res: any = {};
            try {
                res = await deleteDocumentMutation({ id: document._id, force })

                if (Object.keys(res).includes("data")) {
                    enqueueSnackbar(`Deleted document Successfully!`, { variant: "success" })
                    dispatch(closeConfirmationDialog());
                    refetch();
                    if (props?.onRegenerate) {
                        props.onRegenerate();
                    }
                    refetchAfterUpdate(isBoardView);
                } else if (Object.keys(res).includes("error")) {
                    // eslint-disable-next-line 
                    throw res.error.data;
                } else {
                    // eslint-disable-next-line 
                    throw "Data not found"
                }
            } catch (error: any) {
                let errorMessage: string = error?.title ?? "Opps! Something went wrong, Unable to delete"
                enqueueSnackbar(errorMessage, { variant: "warning" });
                dispatch(updateCongirmationDialogBody(<DeletePanel
                    message={"Dependency Table of " + document?.sequence_id + "-r" + document?.version}
                    errorMessage={errorMessage}
                    errorMessageStacks={document?.associations ?? []}
                    warningMessage={""}
                />))

                dispatch(updateConfirmationDialogPositiveBtn({
                    positiveBtn: "Force Delete",
                    hidePositiveBtn: !error.title.toLowerCase().includes('linked'),
                    onOk: () => {
                        dispatch(updateCongirmationDialogBody(<DeletePanel
                            message={"Are you sure want to force delete the Document " + document?.sequence_id + "-r" + document?.version + "?"}
                            warningMessage={"Warning: After delete its not possible to retrieve"}
                        />))
                        dispatch(updateConfirmationDialogPositiveBtn({
                            positiveBtn: "Ok",
                            onOk: () => performRowDel(document, true, isBoardView)
                        }))
                    }
                }))
            } finally {
                dispatch(closeBackdrop());
            }
        };

        const openLinkDocument = () => {
            dispatch(openDialog({
                title: "",
                hideNegativeBtn: true,
                hidePositiveBtn: true,
                maxWidth: "md",
                hideCloseIcon: true,
                body: <ViewLinkDocument isPdfOnly={isPdfOnly} refetch={refetchAll} associations={props.associations} callLinkDocument={(...rest: any) => props.linkDocument(...rest, { object_id, object_type })} parentData={props?.parentData} />,
                enablePadding: false
            }));
        };

        const openAssociation = async (document: any) => {
            dispatch(openBackdrop('Checking dependencies...'));
            await getDependencies({ object_id: document?._id, object_type: "document" }).then((res) => {
                if (res.data) {
                    const parentTabs = [
                        { value: 'parents', label: "Parent", options: res.data?.parents?.map((_: any) => ({ ..._, object_details: _?.objects ?? [] })) ?? [] },
                        { value: 'children', label: "Children", options: res.data?.children?.map((_: any) => ({ ..._, object_details: _?.objects ?? [] })) ?? [] },
                        { value: 'others', label: "Others", options: res.data?.others?.map((_: any) => ({ ..._, object_details: _?.objects ?? [] })) ?? [] },
                    ];

                    dispatch(openConfirmationDialog({
                        title: "Dependencies",
                        body: <DeletePanel
                            message={`Dependency Table of ${document?.sequence_id ?? ""}-r${document?.version ?? ""}`}
                            parentTabs={parentTabs}
                        />,
                        hideNegativeBtn: true,
                        hidePositiveBtn: true
                    }));
                } else {
                    throw res.error
                }
            }).catch((err) => {
                console.log('err:', err);
                enqueueSnackbar('Something went wrong unable to fetch the dependencies', { variant: "error" });
            }).finally(() => {
                dispatch(closeBackdrop());
            });
        };

        const giveMeRows = () => {
            if (!isFetching && data) {
                return data ?? [];
            }
            return [];
        };

        const ROWS = giveMeRows();

        const updatePublishStatus = (row: any, isBoardView: boolean) => {
            dispatch(
                openConfirmationDialog({
                    title: "Publish Operation",
                    body: <DeletePanel
                        message={`Are you sure you want to publish the document ${row?.sequence_id}-r${row?.version}?`}
                        warningMessage={`You will not be able to edit this revision after publishing. You can however create new revisions.`}
                    />,
                    positiveBtn: "Confirm",
                    negativeBtn: "Cancel",
                    onOk: () => proceedToPublish(row, isBoardView),
                    onNegativeBtn: () => dispatch(closeConfirmationDialog())
                })
            );
        };

        const proceedToPublish = (row: any, isBoardView: boolean) => {
            dispatch(closeConfirmationDialog());
            dispatch(openDialog({
                title: "",
                maxWidth: "md",
                body: <EntityPublish isDocument canonical_name={TAG_CANONICAL_NAMES.doc_status} {...row} refetch={() => {
                    setIsPublished(true);
                    refetchAfterUpdate(isBoardView);
                }} object_type="document" object_id={row?._id} />,
                hidePositiveBtn: true,
                hideNegativeBtn: true,
                enablePadding: false
            }));
        };

        const unlinkDocument = (row: any, isBoardView: boolean, object_id: string, object_type: string) => {
            dispatch(
                openConfirmationDialog({
                    title: "Unlink Document",
                    body: <DeletePanel
                        message={`Are you sure you want to unlink the document "${row?.sequence_id}-r${row?.version}"?`}
                    />,
                    positiveBtn: "Unlink",
                    negativeBtn: "Cancel",
                    onOk: () => {
                        if (props?.unlinkDoc) {
                            props.unlinkDoc(row?._id, () => {
                                refetch(object_id, object_type);
                                refetchAfterUpdate(isBoardView);
                            }, row)
                            dispatch(closeConfirmationDialog())
                        }
                    },
                    onNegativeBtn: () => dispatch(closeConfirmationDialog())
                })
            );
        };

        const onReset = () => {
            setSearchByKeyword("");
        };

        const openMoreFilter = () => {
            dispatch(openDrawer({
                isLarge: true,
                component: <Filters enableKeywordSearch searchByKeyword={searchByKeyword} appliedFilters={{}} onApply={() => { }} onCancel={() => { }} onFind={(val: string) => {
                    setSearchByKeyword(val)
                    dispatch(closeDrawer());
                }} onReset={onReset} />
            }))
        };

        const openMovingWindow = (documents: IDoc[]) => {
            dispatch(openDialog({
                title: "",
                maxWidth: "md",
                body: <LinkTo refetch={refetchAll} documents={documents} shouldMove links={rowSelectionModel} clearSelectionHandler={() => setRowSelectionModel([])} />,
                hidePositiveBtn: true,
                hideNegativeBtn: true,
                enablePadding: false
            }));
        };

        const openLinkingWindow = (documents: IDoc[]) => {
            dispatch(openDialog({
                title: "",
                maxWidth: "md",
                body: <LinkTo refetch={refetchAll} documents={documents} links={rowSelectionModel} clearSelectionHandler={() => setRowSelectionModel([])} />,
                hidePositiveBtn: true,
                hideNegativeBtn: true,
                enablePadding: false
            }));
        };

        const handleDuplicate = async (data: any, isBoardView: boolean) => {

            dispatch(clearSelectedChip());
            dispatch(openBackdrop('Preparing for duplicate...'));

            await getDependencies({ object_id: data._id, object_type: "document" }).then((res) => {
                if (res.data) {

                    const parentTabs = [
                        { value: 'parents', label: "Parent", options: res.data?.parents?.map((_: any) => ({ ..._, object_details: _?.objects ?? [] })) ?? [] },
                        { value: 'children', label: "Children", options: res.data?.children?.map((_: any) => ({ ..._, object_details: _?.objects ?? [] })) ?? [] },
                        { value: 'others', label: "Others", options: res.data?.others?.map((_: any) => ({ ..._, object_details: _?.objects ?? [] })) ?? [] },
                    ];

                    const mapped: Partial<SelectedDependency> = {};
                    [
                        ...parentTabs[0].options,
                        ...parentTabs[1].options,
                        ...parentTabs[2].options,
                    ].forEach(e => {
                        mapped[e.object_type as DepKeys] = e.object_details.filter((_: any) => {
                            if (_.released) {
                                return false;
                            } else {
                                return true;
                            }
                        }).map((_: any) => _._id)
                    });

                    dispatch(selectAllChip(mapped as Partial<SelectedDependency>));

                    let body = <Stack spacing={2} padding={2}>
                        <Typography variant="body1">
                            {`Do you want to duplicate the document (${data.sequence_id}-r${data.version})?`}
                        </Typography>

                        <GreyAccordian title='Select Dependencies' content={
                            <DeletePanel
                                message={``}
                                errorMessage={""}
                                warningMessage={"Duplicated document will be attached with selected dependencies"}
                                chipSelectable={true}
                                parentTabs={parentTabs}
                            />
                        } />

                        <Stack direction={"row"} spacing={1} justifyContent={"flex-end"}>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => dispatch(closeDialog())}
                            >
                                Cancel
                            </Button>

                            <Button
                                variant="contained"
                                size="small"
                                onClick={() => performDuplicate(data, isBoardView)}
                            >
                                Duplicate
                            </Button>
                        </Stack>
                    </Stack>;

                    dispatch(
                        openDialog({
                            title: "Are you sure?",
                            body,
                            enablePadding: false,
                            hideNegativeBtn: true,
                            hidePositiveBtn: true,
                        })
                    );

                } else {
                    throw res.error
                }
            }).catch((err) => {
                console.log('err:', err);
                enqueueSnackbar('Something went wrong unable to fetch the dependencies', { variant: "error" });
            }).finally(() => {
                dispatch(closeBackdrop());
            });
        };

        const performDuplicate = async (data: any, isBoardView: boolean) => {
            dispatch(openBackdrop(`Creating duplicate of "${data.sequence_id}" document....`));
            let res: any = {};
            try {
                const payload = giveMeLinksAndUnlinksForDocuments(data);
                res = await duplicateDocument({ id: data?._id, payload });
                if (Object.keys(res).includes("data")) {
                    enqueueSnackbar(
                        res?.data?.message,
                        { variant: "success" }
                    );
                    if (payload.links?.filter(l => l?.object_ids?.length > 0)?.length <= 0) {
                        dispatch(dispatch(
                            openConfirmationDialog({
                                title: "Duplicated Document",
                                body: `The duplicated document can be found under "Documents Module/Documents." Click "OK" to navigate there.`,
                                positiveBtn: "Ok",
                                negativeBtn: "Cancel",
                                onOk: () => {
                                    const formattedTags = giveMeLocationAndDocumentTags(data?.tags_info);
                                    dispatch(closeConfirmationDialog())
                                    dispatch(closeDialog())
                                    navigate(`${AppRoutes.documents}?id=${formattedTags?.location_tags?.value}&folder=${formattedTags?.location_tags?.label}&group_id=${formattedTags?.location_tags?.tag_group_id}&tab=folder`, {
                                        state: {
                                            _id: res?.data?._id
                                        }
                                    })
                                },
                                onNegativeBtn: () => {
                                    dispatch(closeConfirmationDialog())
                                    dispatch(closeDialog())
                                    refetch();
                                    if (props?.onRegenerate) {
                                        props.onRegenerate();
                                    }
                                },
                            })
                        ))

                    } else {
                        refetchAfterUpdate(isBoardView);
                        refetch();
                        if (props?.onRegenerate) {
                            props.onRegenerate();
                        }
                        dispatch(closeConfirmationDialog());
                        dispatch(closeDialog())
                    }
                } else if (Object.keys(res).includes("error")) {
                    // eslint-disable-next-line
                    throw "Unable to create duplicate";
                } else {
                    // eslint-disable-next-line
                    throw "Data not found";
                }
            } catch (error) {
                let errorMessage: string = `Unable to create duplicate of ${data.sequence_id} document!`;
                enqueueSnackbar(errorMessage, { variant: "error" });
            } finally {
                dispatch(closeBackdrop());
            }
        };

        const bulkDownload = async () => {
            try {
                dispatch(openBackdrop('Starting Download....'))
                let response: any = {};
                if (rowSelectionModel.length === 1) {
                    response = await getDocumentAPI({ document_id: rowSelectionModel[0] as string, url_type: "attachment" });
                } else {
                    response = await downloadDocuments({ document_ids: rowSelectionModel as string[], file_name: `All ${CapitalizeString(props.objectType)} Documents` });
                }
                if (response.hasOwnProperty("error")) {
                    setOpenErrorAlert(true)
                } else if (rowSelectionModel.length === 1) {
                    const link = document.createElement('a');
                    link.href = response.data.url;
                    link.setAttribute('download', response.data.file_name); // Set the filename dynamically if needed
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    enqueueSnackbar('Downloaded Successfully!', { variant: "success" })
                } else if (rowSelectionModel.length > 1) {
                    enqueueSnackbar('Download Started', { variant: "success" })
                }
            } catch (error) {
                console.error('error:', error)
                enqueueSnackbar('Something went wrong, unable to start download', { variant: "error" })
            } finally {
                dispatch(closeBackdrop())
            }
        };

        // const giveMeBreakPoints = (component: 'search' | 'addLink') => {
        //     switch (component) {
        //         case 'search':
        //             if (showFilter && showRevision) {
        //                 return {
        //                     sm: 4
        //                 }
        //             } else if (showFilter) {
        //                 return {
        //                     sm: 6
        //                 }
        //             } else if (showRevision) {
        //                 return {
        //                     sm: 4
        //                 }
        //             } else {
        //                 return {
        //                     sm: 8
        //                 }
        //             };

        //         case 'addLink':
        //             if (showFilter && showRevision && showSearch) {
        //                 return {
        //                     sm: 4
        //                 }
        //             } else if ((showFilter || showRevision) && showSearch) {
        //                 return {
        //                     sm: 5
        //                 }
        //             } else {
        //                 return {
        //                     sm: 3
        //                 }
        //             };

        //         default:
        //             return {}
        //     }
        // };

        const forceRelease = async (document: any) => {
            dispatch(openConfirmationDialog({
                title: 'Are you sure?',
                body: "Do you want to force release this diagram, that is locked by '" + document.activity?.user_name + "'?",
                hideNegativeBtn: true,
                positiveBtn: "Release",
                onOk: async () => {
                    dispatch(closeConfirmationDialog());
                    dispatch(openBackdrop('Releasing the diagram...'));
                    await updateDocumentEditStatus({ documentId: document._id ?? "", activityType: 'close' }).then(() => {
                        refetch();
                        window.open(AppRoutes.drawioDiagramEditor(document._id), "_blank");
                    });
                    dispatch(closeBackdrop());
                }
            }))
        };

        const handleShare = (row: any) => {
            dispatch(openDialog({
                title: "",
                maxWidth: "md",
                body: <DocumentShare title={`${row?.sequence_id}-r${row?.version}`} documentId={row?._id} />,
                hidePositiveBtn: true,
                hideNegativeBtn: true,
                enablePadding: false
            }));
        };

        const handleDataSheet = (row: any, board?: IBoard) => {
            setOpenDs({
                row: row,
                board: board
            });
        };

        const giveMeLinksAndUnlinks = (links: any[], unlinks: any[], object_type: string): linksData => {
            let obj: linksData = {
                links: [{ object_ids: links, object_type: object_type }],
                unlinks: [{ object_ids: unlinks, object_type: object_type }],
            };
            return obj;
        };

        const callLinkDocument = async (object_type: string, row: any, links?: any[], unlinks?: any[]) => {
            let action = (unlinks || [])?.length > 0 ? "Unlinking" : "Linking";
            let actionSuccess = (unlinks || [])?.length > 0 ? "Unlinked" : "Linked";
            let actionError = (unlinks || [])?.length > 0 ? "Unlink" : "Link";
            dispatch(openBackdrop(`${action} the ${object_type}...`));
            const linkedData = giveMeLinksAndUnlinks(links || [], unlinks || [], object_type);
            let res: any = {};
            try {
                if (action === "Linking") {
                    res = await linkDocument({ object_id: row?._id, object_type: "document", payload: { links: linkedData.links } })
                } else {
                    res = await unlinkDoc({ object_id: row?._id, object_type: "document", payload: { unlinks: linkedData.unlinks } });
                }

                if (Object.keys(res).includes("data")) {
                    enqueueSnackbar(`${actionSuccess} ${object_type} Successfully!`, { variant: "success" })
                    dispatch(closeConfirmationDialog());
                    refetchAll();
                    setKey(UUIDv4());
                } else if (Object.keys(res).includes("error")) {
                    // eslint-disable-next-line
                    throw res.error.data;
                }
            } catch (error: any) {
                let errorMessage: string =
                    error?.title ?? error ?? `Oops! Something went wrong, Unable to ${actionError} ${object_type}`;
                enqueueSnackbar(errorMessage, { variant: "error" });
            } finally {
                dispatch(closeBackdrop());
            }
        };

        const handleForceDelete = () => {
            dispatch(openConfirmationDialog({
                title: "Are you sure?",
                body: <Stack>
                    <Typography>Do want to <Chip label="Force Delete" variant="filled" size="small" color="error" /> the selected documents?</Typography>
                </Stack>,
                onOk: () => confirmForceDelete(),
                onNegativeBtn: () => dispatch(closeConfirmationDialog())
            }));
        };

        const confirmForceDelete = async () => {
            dispatch(closeConfirmationDialog());
            dispatch(openBackdrop('Deleting...'));

            await Promise.all(rowSelectionModel.map((value) => deleteDocumentMutation({ id: value, force: true }))).then(res => {
                let errors: { title: string }[] = [];
                let docSuccess: string[] = [];
                res?.forEach((result: any) => {
                    if (Object.keys(result).includes("error")) {
                        // eslint-disable-next-line
                        errors.push(result?.error?.data);
                    } else {
                        docSuccess.push('Deleted Documents Successfully!!!');
                    }
                })
                docSuccess.forEach((succ) => {
                    enqueueSnackbar(succ, { variant: "success" });
                    if (props?.onRegenerate) {
                        props.onRegenerate();
                    }
                })
                errors.forEach((err) => {
                    enqueueSnackbar(err?.title ?? 'Unable to delete the document', { variant: "error" });
                })
                refetchAll();
            }).catch((error) => {
                enqueueSnackbar(error?.title ?? 'Unable to delete the document', { variant: "error" });
            });

            dispatch(closeBackdrop());
        };

        React.useEffect(() => {
            if (isBoardView) {
                refetchTagGroups();
            }
            // eslint-disable-next-line
        }, [isBoardView]);

        React.useEffect(() => {
            if (selectedBoard?.canonical_name && tagGroupResponse.isSuccess) {
                getTags(selectedBoard?.canonical_name);
            }
            // eslint-disable-next-line
        }, [selectedBoard?._id, tagsLoading]);

        React.useEffect(() => {
            refetch();
            props.refetch();
            // eslint-disable-next-line
        }, [isBoardView, paginationModel, searchByKeyword, dropDown, props?.tag_group_id, props.tag_id, JSON.stringify(queryOptions?.sortModel)]);

        React.useEffect(() => {
            refetch();
            props.refetch();
            updateDropDown(versionDropdownOption[0])
            setKey(props._id);
            // eslint-disable-next-line
        }, [props._id]);

        React.useEffect(() => {
            setSearch("");
        }, [paginationModel]);

        React.useEffect(() => {
            localStorage.setItem('selectedChip', JSON.stringify(selectedChip));
            // eslint-disable-next-line
        }, [selectedChip]);

        React.useEffect(() => {
            refetch();
            setKey(props.folderId);
            // eslint-disable-next-line
        }, [props.folderId])

        React.useEffect(() => {
            if (isPublished) {
                refetch();
                setIsPublished(false);
            }
            // eslint-disable-next-line
        }, [isPublished]);

        React.useEffect(() => {
            const timeout = setTimeout(() => {
                refetch();
            }, 500)

            return () => clearTimeout(timeout)
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [search]);

        const isLinkable = props.unlinkDoc && updatable;
        const hideDelete = hideDeleteBtn ? true : !deletable;

        const COLUMNS = React.useMemo(() => DocumentTableColumn({
            view: (data: any) => openAddEdit(data, true),
            onDelete: (row: any) => deleteDocument(row, isBoardView),
            openAssociation,
            updatePublishStatus: (row: any) => updatePublishStatus(row, isBoardView),
            hideDelete,
            handleAnnotate: props.onAnnotate,
            unlinkDocument: props.unlinkDoc ? (row: any) => unlinkDocument(row, isBoardView, object_id, object_type) : undefined,
            publishable,
            isLinkable,
            disablePreview: !readable,
            showDuplicate: creatable,
            handleDuplicate: (row: any) => handleDuplicate(row, isBoardView),
            handleDataSheet,
            forceRelease,
            handleShare,
            uploads,
            onDocumentUploadComplete: props.onDocumentUploadComplete,
            isCommonModule
            // eslint-disable-next-line
        }), [JSON.stringify(uploads), isBoardView, isLinkable, hideDelete, object_id, object_type, props?.onDocumentUploadComplete, isCommonModule]);

        const errorResponse = JSON.parse((docBulkDownloadRes?.error as any)?.data || "{}");

        return <>
            {isBoardView && hasBoardView
                ?
                <Box sx={{ padding: 1, height: "100%", background: (theme) => theme.palette.background.default }}>
                    <BoardLayout
                        key={key}
                        showLayoutTitle={false}
                        layoutTitleDetails={{
                            showTitle: false,
                            title: "Documents",
                            breadcrumbs: [],
                            currentPath: "Documents"
                        }}
                        boardViewDetails={{
                            isLoading,
                            entityType: "document",
                            boards: boardTypes,
                            stack: isCommonModule ? false : true,
                            onClick: (board: IBoard, item: IItem) => openAddEdit(item, true),
                            refetchBoards: refetchTagGroups,
                            getTitle(d: IItem) {
                                return (
                                    <Stack direction="row" sx={{
                                        alignItems: "center"
                                    }}>
                                        <Typography
                                            id="view-documents"
                                            onClick={() => openAddEdit(d, true)}
                                            variant="caption"
                                            color="primary"
                                        >
                                            {`${d.title}-r${(d as any)?.version ?? 0}`}
                                        </Typography>
                                        <Tooltip title={d?.released ? "Published" : publishable ? "Work in progress, click to publish the item" : "Not Published"} >
                                            <Stack
                                                id={d?.released ? `doc-publish-success` : `doc-unpublish`}
                                                sx={{
                                                    cursor: d?.released ? "default" : "pointer",
                                                    marginLeft: '4px'
                                                }}
                                            >
                                                {d?.released
                                                    ? <PublishedIcon sx={{ width: 24, height: 24, marginTop: '-2px !important' }} />
                                                    : <WIPIcon onClick={() => publishable ? updatePublishStatus(d, isBoardView) : true} sx={{ width: 24, height: 24, marginTop: '-2px !important' }} />}
                                            </Stack>
                                        </Tooltip>
                                    </Stack>
                                );
                            },                           
                            tagFilterOption: { scope: "document" },
                            creatable,
                            createBtnName: "Add Document",
                            createBtnProps: {
                                id: "add-document",
                                variant: "contained",
                                color: "primary",
                                size:  "small",
                                // sx: { maxWidth: 100 },
                                disabled: isLoading,
                                // fullWidth: true,
                                // startIcon: <></>,
                                // endIcon: <Add />,
                            },
                            onCreateBtn: (e) => handleClick(e),
                            actions: (data, board) => {
                                return [
                                    <Stack direction={"row"} alignItems={"center"} sx={{ width: 'fit-content', flexWrap: 'wrap' }}>
                                        <DocumentActions
                                            row={data}
                                            uploads={uploads}
                                            forceRelease={forceRelease}
                                            handleDuplicate={creatable ? (row: any) => handleDuplicate(row, isBoardView) : undefined}
                                            handleShare={handleShare}
                                            disablePreview={!readable}
                                            handleDataSheet={(row) => handleDataSheet(row, board)}
                                        />
                                        {unlinkDocument && isLinkable && <Tooltip title={`Unlink the Document "${data?.title}"`}>
                                            <IconButton onClick={(e) => {
                                                e.stopPropagation();
                                                unlinkDocument(data, isBoardView, object_id, object_type);
                                            }}>
                                                <LinkOff color={"action"} />
                                            </IconButton>
                                        </Tooltip>}
                                        {!hideDelete && <Tooltip title={data?.released ? `${data?.title} Revision is published` : `Delete the Document ${data?.title}`}>
                                            <IconButton disableRipple={data?.released} onClick={(e) => {
                                                e.stopPropagation();
                                                if (!data?.released) {
                                                    deleteDocument(data, isBoardView);
                                                }
                                            }}>
                                                <DeleteOutline color={data?.released ? "disabled" : "error"} />
                                            </IconButton>
                                        </Tooltip>}
                                    </Stack>
                                ]
                            },
                            searchDetails: {
                                search,
                                onSearch: setSearch,
                                searchPlaceholder: "Search for Documents"
                            },
                            fetchBoardItems: async (board: IBoard, page: number, rowsPerPage: number, search: string, filters) => {
                                const { data: boardData = { data: [], totalCount: 0 }, ...rest } = await getDocuments({
                                    search: search.trim() ? search : "",
                                    page: page - 1,
                                    pageSize: rowsPerPage,
                                    tag_id: [board.id, ...filters?.tags?.map((t) => t.value) ?? []].join(','),
                                    include_all: true,
                                    include_history: dropDown?.value ?? false,
                                    include_assoc: includeAssoc,
                                    object_id: object_id,
                                    object_type: object_type,
                                });
                                return { ...boardData, ...rest };
                            },
                            onUpdateItemBoard: async (result, item: any) => {
                                const dropTag = boardTypes.find((b: IBoard) => b?.id === result?.destination?.droppableId);
                                if (dropTag) {
                                    return await updateDocument({ document_id: item?._id, update_version: false, tags_info: [...(item.tags_info ?? []).filter((t: TagInfo) => t.tag_group_canonical_name !== (boardTypes?.[0] as any)?.tag_group_canonical_name), dropTag].map((t) => ({ tag_group_id: t.tag_group_id, tag_id: t.tag_id ?? t._id })) as any, skipDataManipulation: true });
                                }
                                return new Promise((resolve, reject) => {
                                    reject({ error: "Unable to move the document" });
                                })
                            },
                        }}
                    />
                </Box>
                : (
                    <MyPaper shadow={"unset"} height={"100%"}>
                        <Box sx={{ position: "relative", height: "100%" }}>
                            <Stack gap={1} sx={{ flexDirection: { sm: "column", md: "row" }, alignItems: { xs: "flex-end", sm: "flex-end", md: 'flex-start' } }} width={"100%"}>
                                {hasBoardView &&
                                    <CurrentView entity={"document"} />
                                }

                                {/* Header */}
                                <Grid flexGrow={1} container spacing={1} alignItems={"center"} justifyContent={"flex-end"} columns={14}>
                                    {/* Download Button */}
                                    <Grid size={{ xs: 6, sm: 3, md: 2, lg: 1 }}>
                                        <Tooltip title={`Download Selected documents`}>
                                            <Button
                                                disabled={rowSelectionModel.length <= 0}
                                                disableRipple
                                                variant="contained"
                                                color="info"
                                                sx={{ minWidth: "8px", width: "100%" }}
                                                onClick={() => bulkDownload()}
                                            >
                                                <Download /> <Box sx={{ display: { xs: "none", sm: "none" } }} component={"span"} style={{ marginLeft: 1 }}>Download {`(${rowSelectionModel.length})`}</Box>
                                            </Button>
                                        </Tooltip>
                                    </Grid>

                                    {/* Filter Icon */}
                                    {false && <Grid size={{ xs: 6, sm: 1 }}>
                                        <Tooltip title={`Advance Filter ${searchByKeyword.length > 0 ? "Applied" : ""}`}>
                                            <Button
                                                disableRipple
                                                variant="outlined"
                                                color="warning"
                                                sx={{ minWidth: "8px", width: "100%" }}
                                                onClick={openMoreFilter}
                                            >
                                                <Badge
                                                    color="warning"
                                                    variant="dot"
                                                    invisible={!(searchByKeyword.length > 0)}
                                                >
                                                    <FilterAltOutlined />
                                                </Badge>
                                            </Button>
                                        </Tooltip>
                                    </Grid>}

                                    {/* Revision Filter */}
                                    {showRevision && <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                                        <FormControl fullWidth>
                                            <InputLabel id="revisionFilterLabel">Revision</InputLabel>
                                            <Select
                                                labelId="revisionFilterLabel"
                                                id="drevisionFilter"
                                                value={dropDown?.value}
                                                size="small"
                                                label="Revision"
                                                onChange={(e) => {
                                                    updateDropDown(e.target.value === "true" ? versionDropdownOption[1] : versionDropdownOption[0])
                                                }}
                                            >
                                                {versionDropdownOption.map((option) => (
                                                    <MenuItem key={option.value} value={option.value}>
                                                        {option.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>}

                                    {/* Search Bar */}
                                    {showSearch && (
                                        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 4 }}>
                                            <SearchFilter
                                                searchPlaceholder={"Search for Documents"}
                                                handleChange={setSearch}
                                                value={search}
                                            />
                                        </Grid>
                                    )}

                                    {/* Add Link Btn */}
                                    {(showCreateBtn && creatable) && (
                                        <Grid size={{ xs: 12, sm: 6, md: 3, lg: 2 }}>
                                            {/* Add Document Button */}
                                            {(showCreateBtn && creatable) && (<Button
                                                id="add-document"
                                                variant="contained"
                                                color="primary"
                                                sx={{ maxWidth: 250 }}
                                                disabled={isLoading}
                                                fullWidth
                                                endIcon={<Add />}
                                                onClick={(e) => handleClick(e)}>
                                                Add
                                            </Button>)}

                                            {/* </Stack> */}
                                        </Grid>
                                    )}
                                    {/* Link Document Button */}
                                    {(showHelperBtn && updatable) && (
                                        <Grid size={{ xs: 12, sm: 6, md: 3, lg: 2 }}>
                                            <Button
                                                id="link-document"
                                                fullWidth
                                                sx={{ maxWidth: 250 }}
                                                variant="contained"
                                                color="primary"
                                                onClick={openLinkDocument}>
                                                Link
                                            </Button></Grid>)}

                                    {/* Link To Button */}
                                    {hasCheckbox && (<Grid size={{ xs: 12, sm: 6, md: 3, lg: 2 }}><Button
                                        id="link-to"
                                        fullWidth
                                        sx={{ maxWidth: 250, paddingLeft: "4px", paddingRight: "4px" }}
                                        variant="contained"
                                        color="primary"
                                        disabled={rowSelectionModel.length <= 0}
                                        onClick={() => openLinkingWindow(data)}>
                                        Link To
                                    </Button></Grid>)}

                                    {hasCheckbox && (<Grid size={{ xs: 12, sm: 6, md: 3, lg: 2 }}><Button
                                        id="move-to"
                                        fullWidth
                                        sx={{ maxWidth: 250, paddingLeft: "4px", paddingRight: "4px" }}
                                        variant="contained"
                                        color="primary"
                                        disabled={rowSelectionModel.length <= 0}
                                        onClick={() => openMovingWindow(data)}>
                                        Move To
                                    </Button></Grid>)}
                                </Grid>
                            </Stack>

                            {/* Error */}
                            <>
                                {isError && <Alert sx={{ mt: 2 }} severity="error">Oops! Something went wrong, Unable to fetch Documents. Try Again Later!</Alert>}

                                {docBulkDownloadRes?.error &&
                                    <Collapse in={openErrorAlert}>
                                        <Alert
                                            sx={{ mt: 2 }}
                                            severity="error"
                                            action={
                                                <IconButton
                                                    aria-label="close"
                                                    color="inherit"
                                                    size="small"
                                                    onClick={() => {
                                                        setOpenErrorAlert(false);
                                                    }}
                                                >
                                                    <Close fontSize="inherit" />
                                                </IconButton>
                                            }
                                        >
                                            <AlertTitle sx={{ fontWeight: "bold", textTransform: 'capitalize' }}>{errorResponse?.title}</AlertTitle>
                                            <Typography variant="caption">The allowed limit for downloadable documents is {formatBytes(errorResponse?.details?.max_size ?? 0)}, but the document you attempted to download is {formatBytes(errorResponse?.details?.total_size_of_docments ?? 0)}.</Typography>
                                        </Alert>
                                    </Collapse>
                                }
                            </>

                            {/* Data Grid */}
                            <Box sx={{ mt: 2, height: 'calc(100% - 54px)' }}>
                                <CustomDataGrid
                                    saveLocal={false}
                                    rows={ROWS}
                                    columns={COLUMNS}
                                    loading={loading}
                                    pagination
                                    getRowId={(row) => row._id}
                                    showToolbar={true}
                                    rowCount={totalCount}
                                    paginationModel={paginationModel}
                                    paginationMode="server"
                                    onPaginationModelChange={setPaginationModel}
                                    checkboxSelection={true}
                                    onRowSelectionModelChange={(newRowSelectionModel) =>
                                        setRowSelectionModel(newRowSelectionModel)
                                    }
                                    rowSelectionModel={rowSelectionModel}
                                    sortingMode="server"
                                    onSortModelChange={handleSortModelChange}
                                    showDeleteBtn={true}
                                    disableDeleteBtn={rowSelectionModel.length === 0}
                                    onDeleteBtnClicked={handleForceDelete}
                                />
                            </Box>
                        </Box>
                    </MyPaper>
                )}

            <Menu
                id="add-document-menu"
                anchorEl={menuEl}
                open={open}
                onClose={handleClose}
            >
                {fileSelectOptions.filter(menu => {
                    if (isPdfOnly) {
                        return ['single', 'multiple'].includes(menu.value);
                    } else {
                        return true;
                    }
                }).map(menu => (
                    <MenuItem key={menu.value} onClick={() => {
                        if (!isLoading) {
                            openAddEdit(null, false, menu?.value as FileSelectionOptionType);
                        }
                        handleClose();
                    }}>
                        <Typography variant="body2" >{menu?.label}</Typography>
                        <>{menu.isBeta && <img style={{ marginLeft: "8px" }} src={'/beta.gif'} alt='In Beta' />}</>
                    </MenuItem>
                ))}
            </Menu>

            {/* MDG Dialog */}
            <Dialog
                key={sheet_id || (openDs || { row: { _id: "datasheet" } })?.row?._id || "datasheet"}
                maxWidth={"xl"}
                fullWidth={true}
                open={!!openDs}
                onClose={handleDsClose}
                TransitionComponent={Transition}
            >
                {/* Description Generator */}
                <DialogContent sx={{ padding: 0, minHeight: "90vh" }}>
                    <DataTableForm forDocument onClose={handleDsClose} sheet_id={sheet_id} type="datasheet" associations={[{ object_type: "document", object_details: [{ ...(openDs?.row || {}) }] }]} preview={(openDs as any)?.released || !updatable} refetch={refetchAll} linkDataTableTemplate={(links: any[], unlinks: any[]) => callLinkDocument('dataTableForm', openDs?.row, links, unlinks)} />
                </DialogContent>
            </Dialog>
        </>
    };